import { useEffect, useLayoutEffect, useMemo, useState } from "react";
import sortUp from "../../images/sortUp.svg";
import {
  Checkbox,
  FormControl,
  MenuItem,
  Select,
  Typography,
  createTheme,
  ThemeProvider,
  Popover,
  Tooltip,
} from "@mui/material";
import "./RcaTable.css";
import { Pagination } from "../../components/Pagination";
import SelectableList from "./PopOverComp";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "../../store/store";
import { rcaWriteBackApi } from "../../api/forecastPageApi";
import { useOktaAuth } from "@okta/okta-react";
import { updateExecutionBackTestingPageNumber } from "../../store/rcaSlice";
import loadingSvg from "../../images/bouncing-circles.svg";
import TableContainer from "../../components/common/TableContainer";
import { downloadExcelFile } from "../../api/forecastPageApi";
import DownloadButton from "../../components/common/DownloadButton";
import { setForecastFilter } from "../../store/filterSlice";
import { setSelectedRows } from "../../store/rcaSlice";
const uniqueKey = require("unique-key");

type DataRow = {
  upc: string;
  salesZone?: string;
  cluster: string;
  account: string;
  region: string;
  rca: string;
  finalRca: string;
  pfeAccuracy: number;
  pfeBias: number;
  forecast: number;
  error: number;
  dpAccuracy: number;
  id: string;
  upcDesc: string;
  salesRegionName: string;
  absError: number;
  keyAccountName: string;
  model: string;
  featureImportance: string;
  dpForecastAccuracy: number;
  dpForecastBias: number;
  forecastValueAdd: number;
  rcaTotal: string;
  tag: string;
  locationId: string;
  locationName: string;
};

type SortKey = keyof DataRow;

// Custom Toast component
const Toast = ({
  message,
  type,
  onClose,
}: {
  message: string;
  type: "success" | "error";
  onClose: () => void;
}) => {
  const toastClass =
    type === "success"
      ? "bg-secondary-bg outline outline-green-500"
      : "bg-secondary-bg outline outline-red-500";

  return (
    <div
      className={`fixed top-4 right-4 p-4 rounded shadow ${toastClass}`}
      style={{ zIndex: 1000 }}
    >
      <span>{message}</span>
      <button
        onClick={onClose}
        className="ml-4 font-bold text-xl focus:outline-none"
      >
        &times;
      </button>
    </div>
  );
};

const RCATable = () => {
  const [data, setTableData] = useState<any>([]);
  const [listOverride, setListItems] = useState<any>([
    { id: 1, label: "Select Override" },
  ]);
  const [isSaving, setIsSaving] = useState(false);
  const [anchorEl1, setAnchorEl1] = useState<null | HTMLElement>(null);
  const [anchorElTotal, setAnchorElTotal] = useState<null | HTMLElement>(null);
  const [sortKey, setSortKey] = useState<SortKey | null>(null);
  const [ascending, setAscending] = useState<boolean>(true);
  const selectedRows = useSelector(
    (state: RootState) => state.rcaData.selectedRows
  );
  const [selectValue, setSelectValue] = useState<{ [key: string]: string }>({});
  const [selectValueTotal, setSelectValueTotal] = useState<{
    [key: string]: string;
  }>({});

  // Toast state for notifications
  const [toast, setToast] = useState<{
    visible: boolean;
    message: string;
    type: "success" | "error" | "";
  }>({ visible: false, message: "", type: "" });

  const userData = useSelector((state: RootState) => state.user);
  const { currentSector } = userData;

  const tableData = useSelector(
    (state: RootState) => state?.rcaData?.tableData
  );
  const dropDown = useSelector((state: RootState) => state?.rcaData);
  const filter = useSelector((state: RootState) => state?.forecastFilter);
  const payload: any = {
    shortTermMidTermCode: filter?.term.toString(),
    snapshotDate:
      filter?.term.toString() === "ST"
        ? filter?.shortSnapshotDate
        : filter?.midSnapshotDate,
    brandName: filter?.brand,
    subBrandName: filter?.subBrand,
    productLineName: filter?.productLine,
    productGroupName: filter?.productGroup,
    materialGlobalTradeItemNumberDescription: filter?.planningItem,
    packageSizeName: filter?.size,
    productCategoryName: filter?.category,
    portfolioGroupName: filter?.mg4PortfolioGroup,
    packageName: filter?.count,
    packTypeName: filter.ppg,
    keyAccount: filter?.keyAccount,
    customerGlobalName: filter?.global,
    customerSectorNumber: filter?.customerSector,
    customerRegionName: filter?.customerRegion,
    customerGroupName: filter?.customerGroup,
    customerBusinessUnitName: filter?.businessUnit,
    customerMarketUnitName: filter?.marketUnit,
    ibpDemandGroup: filter?.ibpDemandGroup,
    customerTradeSegmentDescription: filter?.cg1TrageSegment,
    salesSectorName: filter?.salesSector,
    salesRegionName: filter?.salesRegion,
    salesCountryCode: filter?.salesCountry,
    salesOrganizationCode: filter?.businessOrg,
    salesDivisionName: filter?.division,
    forecastEngineClusterId: filter?.clusters,
    forecastModuleCode: filter?.ac1ac2,
    forecastEngineSegmentationId: filter?.segment,
    packageTypeName: filter?.container,
    sector: userData?.currentSector?.sector,
    market: userData?.currentSector?.market,
    location: filter?.location,
    // LATAM Key
    flavorName: filter?.flavourNames,
    customerChannelName: filter?.channels,
    customerDemandGroup: filter?.demandGroups,
    customerSubChannelName: filter?.subChannels,
    customerRegionalCluster: filter?.regionalClusters,
    locationGlobalName: filter?.globalCode,
    locationSectorName: filter?.locationSector,
    locationCountryName: filter?.locationCountry,
    planningLocationId: filter?.planningLocationId,
    locationRegionName: filter?.regionCode,
    // PFNA key
    alternateIBPDemandGroup: filter?.alternateIBPDemandGroup,
    salesZoneName: filter?.zone,
    eachUPC: filter?.eachUPC,
  };

  const [newTagInput, setNewTagInput] = useState<string>("");
  const [anchorElTag, setAnchorElTag] = useState<null | HTMLElement>(null);
  const [selectValueTag, setSelectValueTag] = useState<{
    [key: string]: string;
  }>({});
  const [tagOptions, setTagOptions] = useState<any[]>([]);

  const { authState } = useOktaAuth();
  const isTableLoading = useSelector(
    (state: RootState) => state?.rcaData?.isTableDataLoading
  );
  const rcaExecutionPaginationSlice = useSelector(
    (state: RootState) => state?.rcaExecutionPaginationSlice
  );
  const dispatch = useDispatch();

  useLayoutEffect(() => {
    dispatch(setForecastFilter({ key: "rcaTag", value: [] }));
  }, []);

  const showToast = (message: string, type: "success" | "error") => {
    setToast({ visible: true, message, type });
    setTimeout(() => {
      setToast({ visible: false, message: "", type: "" });
    }, 5000);
  };

  const extractTableData = async () => {
    setIsSaving(true);

    const rowsToProcess = sortedData.filter((row: any) => {
      const rowId = row.id;

      const statChanged =
        selectValue[rowId] &&
        selectValue[rowId] !== "Select Override" &&
        selectValue[rowId] !== row.rca;

      const totalChanged =
        selectValueTotal[rowId] &&
        selectValueTotal[rowId] !== "Select Override" &&
        selectValueTotal[rowId] !== "Select Total" &&
        selectValueTotal[rowId] !== row.rcaTotal;

      const tagChanged =
        selectValueTag[rowId] && selectValueTag[rowId] !== row.tag;

      return statChanged || totalChanged || tagChanged;
    });

    if (rowsToProcess.length === 0) {
      // showToast("No new changes to override", "error");
      setIsSaving(false);
      return;
    }

    const extractedData = rowsToProcess.map((row: any) => {
      const rowId = row.id;
      const payloadEntry: any = {
        materialGlobalTradeItemNumber: row?.upc,
        demandPlanningKeyAccountId: row?.account,
        salesRegionCode: row?.region,
        forecastTargetDate: row?.snapshotDate
          ? [row?.snapshotDate].toString()
          : null,
        shortTermMidTermCode: filter?.term.toString(),
        sector: currentSector?.sector,
        market: currentSector?.market,
      };

      const statOverride = selectValue[rowId];
      const totalOverride = selectValueTotal[rowId];
      const tag = selectValueTag[rowId];

      if (
        statOverride &&
        statOverride !== "Select Override" &&
        statOverride !== row.rca
      ) {
        payloadEntry.rcaStatOverride = statOverride;
      }

      if (
        totalOverride &&
        totalOverride !== "Select Override" &&
        totalOverride !== "Select Total" &&
        totalOverride !== row.rcaTotal
      ) {
        payloadEntry.rcaTotalOverride = totalOverride;
      }

      if (currentSector?.sector === "PFNA") {
        payloadEntry.forecastModuleCode = filter?.ac1ac2?.[0];
      }
      if (tag && tag !== row.tag) {
        payloadEntry.rcaTag = tag;
      }

      return payloadEntry;
    });

    for (const data of extractedData) {
      try {
        const response = await new Promise((resolve, reject) => {
          setTimeout(async () => {
            try {
              const res = await rcaWriteBackApi(data, authState);
              resolve(res);
            } catch (err) {
              reject(err);
            }
          }, 1000);
        });

        if (
          response &&
          typeof response === "object" &&
          "code" in response &&
          (response as any).code === 400
        ) {
          console.error("Write-back API call failed with code 400");
          showToast("Override failed", "error");
          continue;
        }

        if (
          response &&
          typeof response === "object" &&
          "message" in response &&
          (response as any).message.toLowerCase().includes("trace id")
        ) {
          console.error("Override failed:", response.message);
          showToast("Override failed", "error");
        } else {
          showToast("Override success", "success");
        }
      } catch (error: any) {
        console.error("Error in write-back API call:", error);
        showToast("Override failed", "error");
      }
    }

    setIsSaving(false);
  };

  const transformResponseData = (responseData: any) => {
    const PBNA_transformedData = responseData?.map((item: any) => ({
      upc: item.materialGlobalTradeItemNumber,
      account: item.demandPlanningKeyAccountId,
      region: item.salesRegionCode,
      cluster: item?.forecastEngineClusterId,
      rca: item.rcaStat,
      finalRca: item.finalRcaName,
      pfeAccuracy: item.pfeAccuracy,
      pfeBias: item.pfeBias,
      forecast: Math.trunc(item.statisticalForecast),
      snapshotDate: item?.snapshotDate,
      absError: item?.absoluteErrorQuantity,
      salesRegionName: item?.salesRegionName,
      upcDesc: item?.materialGlobalTradeItemNumberDescription,
      id: uniqueKey(),
      tag: item?.tag,
      keyAccountName: item?.demandPlanningKeyAccountName,
      model: item?.model,
      dpForecastAccuracy: Math.trunc(item?.dpForecastAccuracy),
      dpForecastBias: Math.trunc(item?.dpForecastBias),
      forecastValueAdd: Math.trunc(item?.forecastValueAdd),
      rcaTotal: item?.rcaTotal,
      featureImportance: item?.featureImportance,
      locationId: item?.locationId,
      locationName: item?.locationName,
    }));

    const PFNA_transformedData = responseData?.map((item: any) => ({
      upc: item.businessReportingMaterialId,
      salesZone: item.businessReportingGeographyName,
      account: item.businessReportingCustomerId,
      region: item.salesRegionCode,
      cluster: item?.forecastEngineClusterId,
      rca: item.rcaStat,
      finalRca: item.finalRcaName,
      pfeAccuracy: item.pfeAccuracy,
      pfeBias: item.pfeBias,
      forecast: Math.trunc(item.statisticalForecast),
      snapshotDate: item?.snapshotDate,
      absError: item?.absoluteErrorQuantity,
      salesRegionName: item?.salesRegionName,
      upcDesc: item?.materialGlobalTradeItemNumberDescription,
      id: uniqueKey(),
      tag: item?.tag,
      keyAccountName: item?.businessReportingCustomerName,
      model: item?.model,
      dpForecastAccuracy: Math.trunc(item?.dpForecastAccuracy),
      dpForecastBias: Math.trunc(item?.dpForecastBias),
      forecastValueAdd: Math.trunc(item?.forecastValueAdd),
      rcaTotal: item?.rcaTotal,
      featureImportance: item?.featureImportance,
      locationId: item?.locationId,
      locationName: item?.locationName,
    }));

    if (currentSector?.sector === "PBNA") {
      return PBNA_transformedData;
    } else if (currentSector?.sector === "PFNA") {
      return PFNA_transformedData;
    }
  };

  const handleUpdateTag = (selectedValue: string | null) => {
    if (!selectedValue) return;
    const updatedValues = { ...selectValueTag };
    selectedRows?.forEach((index) => {
      const rowId = data[index]?.id;
      if (rowId) {
        updatedValues[rowId] = selectedValue;
      }
    });
    setSelectValueTag(updatedValues);
    setAnchorElTag(null);
  };

  const sortData = (key: SortKey, ascending: boolean) => {
    return [...data]?.sort((a, b) => {
      if (ascending) {
        return a[key] > b[key] ? 1 : -1;
      } else {
        return a[key] < b[key] ? 1 : -1;
      }
    });
  };

  const sortedData = useMemo(() => {
    if (!sortKey) return data;
    return sortData(sortKey, ascending);
  }, [data, sortKey, ascending]);

  useEffect(() => {
    const transformed = transformResponseData(tableData);
    setTableData(transformed);

    setListItems(dropDown?.rcaStats);

    setTagOptions(dropDown?.rcaTags);
  }, [tableData]);

  const theme = createTheme({
    palette: {
      background: { paper: "#272727" },
      text: { primary: "#ffff" },
    },
  });

  const handleSelectChange = (id: string, event: any, type: any) => {
    if (type === "summary") {
      setSelectValue((prev) => ({
        ...prev,
        [id]: event?.target?.value,
      }));
    } else {
      setSelectValueTotal((prev) => ({
        ...prev,
        [id]: event?.target?.value,
      }));
    }
  };

  const handleUpdateTotal = (selectedValue: string | null) => {
    if (!selectedValue) return;
    const updatedValues = { ...selectValueTotal };
    selectedRows?.forEach((index) => {
      const rowId = data[index]?.id;
      if (rowId) {
        updatedValues[rowId] = selectedValue;
      }
    });
    setSelectValueTotal(updatedValues);
    setAnchorElTotal(null);
  };

  const handleUpdate = (selectedValue: string | null) => {
    if (!selectedValue) return;
    const updatedValues = { ...selectValue };
    selectedRows.forEach((index) => {
      const rowId = data[index]?.id;
      if (rowId) {
        updatedValues[rowId] = selectedValue;
      }
    });
    setSelectValue(updatedValues);
    setAnchorEl1(null);
  };

  const handleSort = (key: SortKey) => {
    setAscending(sortKey === key ? !ascending : true);
    setSortKey(key);
  };

  const getColor = (value: number) => {
    if (value <= 70) return "bg-red-700 text-white";
    if (value >= 78) return "bg-green-bg text-white";
    return "bg-yellow-bg text-white";
  };

  const renderSortIcon = (key: SortKey) => {
    if (sortKey === key) {
      return ascending ? (
        <img src={sortUp} alt="sortUp" className="ml-2 w-3 h-3" />
      ) : (
        <img src={sortUp} alt="sortDown" className="ml-2 w-3 h-3" />
      );
    } else {
      return <img src={sortUp} alt="sortDown" className="ml-2 w-3 h-3" />;
    }
  };

  const handleCheckboxChange = (index: number) => {
    const currentSelectedRows = selectedRows || [];
    const newSelectedRows = [...currentSelectedRows];
    const selectedIndex = currentSelectedRows.indexOf(index);

    if (selectedIndex === -1) {
      newSelectedRows.push(index);
    } else {
      newSelectedRows.splice(selectedIndex, 1);
    }

    dispatch(setSelectedRows(newSelectedRows));
  };

  const handlePageNumberUpdateEvent = (currentPageNumber: number) => {
    dispatch(
      updateExecutionBackTestingPageNumber({
        pageLimit: rcaExecutionPaginationSlice?.pageLimit,
        pageNumber: currentPageNumber,
        totalPages: rcaExecutionPaginationSlice?.totalPages,
        isDataShownClicked: false,
        groupBySelectedField: [],
      })
    );
  };

  const handleDownload = () => {
    downloadExcelFile("RcaExecution", authState, {
      ...payload,
      pageNumber: 1,
      pageSize: 1000,
    });
  };

  return (
    <div className="pt-4">
      <TableContainer onDownload={handleDownload} isRCA={true}>
        {isTableLoading ? (
          <div className="flex justify-center">
            <img
              src={loadingSvg}
              alt="loading"
              className="mt-10"
              width={40}
              height={40}
            />
          </div>
        ) : (
          <div>
            <div className="pb-4 flex justify-between items-center">
              {tableData?.length > 0 ? (
                <div className="flex space-x-2">
                  <div className="pt-3 mr-3">
                    <Typography variant="h6">Add</Typography>
                  </div>
                  <ThemeProvider theme={theme}>
                    <div
                      style={{
                        minWidth: "195px",
                        backgroundColor: "#313131",
                        borderRadius: "100px",
                        position: "relative",
                        display: "inline-block",
                      }}
                    >
                      <Tooltip
                        title={
                          selectedRows?.length > 0
                            ? ""
                            : "Select rows to enable"
                        }
                      >
                        <Typography
                          onClick={(event) => {
                            if (selectedRows?.length > 0) {
                              setAnchorEl1(event.currentTarget);
                            }
                          }}
                          className="p-4 flex items-center justify-between"
                          sx={{
                            color:
                              selectedRows?.length > 0 ? "#fff" : "#616161",
                            cursor:
                              selectedRows?.length > 0
                                ? "pointer"
                                : "not-allowed",
                            display: "flex",
                            border: "none",
                            minWidth: "195px",
                          }}
                        >
                          RCA Stat Override
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            className="h-4 w-4 ml-2"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                            strokeWidth={2}
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              d="M19 9l-7 7-7-7"
                            />
                          </svg>
                        </Typography>
                      </Tooltip>
                      <Popover
                        open={Boolean(anchorEl1)}
                        anchorEl={anchorEl1}
                        onClose={() => setAnchorEl1(null)}
                        anchorOrigin={{
                          vertical: "bottom",
                          horizontal: "left",
                        }}
                      >
                        <SelectableList
                          items={listOverride}
                          onCancel={() => setAnchorEl1(null)}
                          onUpdate={handleUpdate}
                        />
                      </Popover>
                    </div>
                  </ThemeProvider>
                  <ThemeProvider theme={theme}>
                    <div
                      style={{
                        minWidth: "195px",
                        backgroundColor: "#313131",
                        borderRadius: "100px",
                        position: "relative",
                        display: "inline-block",
                      }}
                    >
                      <Tooltip
                        title={
                          selectedRows?.length > 0
                            ? ""
                            : "Select rows to enable"
                        }
                      >
                        <Typography
                          onClick={(event) => {
                            if (selectedRows.length > 0) {
                              setAnchorElTotal(event.currentTarget);
                            }
                          }}
                          className="p-4 flex items-center justify-between"
                          sx={{
                            color:
                              selectedRows?.length > 0 ? "#fff" : "#616161",
                            cursor:
                              selectedRows?.length > 0
                                ? "pointer"
                                : "not-allowed",
                            display: "flex",
                            border: "none",
                            minWidth: "195px",
                          }}
                        >
                          RCA Total
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            className="h-4 w-4 ml-2"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                            strokeWidth={2}
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              d="M19 9l-7 7-7-7"
                            />
                          </svg>
                        </Typography>
                      </Tooltip>
                      <Popover
                        open={Boolean(anchorElTotal)}
                        anchorEl={anchorElTotal}
                        onClose={() => setAnchorElTotal(null)}
                        anchorOrigin={{
                          vertical: "bottom",
                          horizontal: "left",
                        }}
                      >
                        <SelectableList
                          items={listOverride}
                          onCancel={() => setAnchorElTotal(null)}
                          onUpdate={handleUpdateTotal}
                        />
                      </Popover>
                    </div>
                  </ThemeProvider>

                  <ThemeProvider theme={theme}>
                    <div
                      style={{
                        minWidth: "195px",
                        backgroundColor: "#313131",
                        borderRadius: "100px",
                        position: "relative",
                        display: "inline-block",
                      }}
                    >
                      <Tooltip
                        title={
                          selectedRows?.length > 0
                            ? ""
                            : "Select rows to enable"
                        }
                      >
                        <Typography
                          onClick={(event) => {
                            if (selectedRows?.length > 0) {
                              setAnchorElTag(event.currentTarget);
                            }
                          }}
                          className="p-4 flex items-center justify-between"
                          sx={{
                            color:
                              selectedRows?.length > 0 ? "#fff" : "#616161",
                            cursor:
                              selectedRows?.length > 0
                                ? "pointer"
                                : "not-allowed",
                            display: "flex",
                            border: "none",
                            minWidth: "195px",
                          }}
                        >
                          Tag
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            className="h-4 w-4 ml-2"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                            strokeWidth={2}
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              d="M19 9l-7 7-7-7"
                            />
                          </svg>
                        </Typography>
                      </Tooltip>

                      <Popover
                        open={Boolean(anchorElTag)}
                        anchorEl={anchorElTag}
                        onClose={() => setAnchorElTag(null)}
                        anchorOrigin={{
                          vertical: "bottom",
                          horizontal: "left",
                        }}
                      >
                        <div className="bg-secondary-bg text-white w-64 p-2">
                          <div className="px-2 py-1">
                            <input
                              type="text"
                              value={newTagInput}
                              maxLength={20}
                              onChange={(e) => setNewTagInput(e.target.value)}
                              placeholder="New Tag"
                              className="bg-gray-800 text-white border border-gray-500 rounded px-2 py-1 w-full text-sm"
                            />
                          </div>

                          <div className="border-t border-gray-600 my-2" />

                          <div className="max-h-48 overflow-y-auto">
                            {tagOptions?.map((item: string, index: number) => (
                              <div
                                key={index}
                                className="flex items-center px-3 py-2 hover:bg-hover cursor-pointer"
                                onClick={() => handleUpdateTag(item)}
                              >
                                <span className="text-sm">{item}</span>
                              </div>
                            ))}
                          </div>

                          <div className="flex justify-end gap-2 mt-2 px-2">
                            <button
                              className="text-sm text-gray-300 px-3 py-1 border border-gray-500 rounded"
                              onClick={() => {
                                setAnchorElTag(null);
                                setNewTagInput("");
                              }}
                            >
                              Cancel
                            </button>
                            <button
                              className="text-sm bg-blue-600 px-3 py-1 rounded text-white"
                              disabled={!newTagInput.trim()}
                              onClick={() => {
                                const cleanedTag = newTagInput.trim();
                                handleUpdateTag(cleanedTag);
                                setNewTagInput("");
                              }}
                            >
                              Update
                            </button>
                          </div>
                        </div>
                      </Popover>
                    </div>
                  </ThemeProvider>

                  <div className="flex justify-end mt-2">
                    <button
                      className="write-back-button flex items-center gap-2"
                      onClick={extractTableData}
                      disabled={isSaving}
                    >
                      {isSaving && (
                        <svg
                          className="animate-spin h-4 w-4 text-white"
                          xmlns="http://www.w3.org/2000/svg"
                          fill="none"
                          viewBox="0 0 24 24"
                        >
                          <circle
                            className="opacity-25"
                            cx="12"
                            cy="12"
                            r="10"
                            stroke="currentColor"
                            strokeWidth="4"
                          ></circle>
                          <path
                            className="opacity-75"
                            fill="currentColor"
                            d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"
                          ></path>
                        </svg>
                      )}
                      Save
                    </button>
                  </div>
                </div>
              ) : (
                <></>
              )}
              <div>
                {tableData?.length > 0 ? (
                  <DownloadButton onClick={handleDownload} />
                ) : (
                  <></>
                )}
              </div>
            </div>

            {tableData?.length > 0 ? (
              <div className="RCA-table-holder">
                <table className="text-xs w-full">
                  <thead>
                    <tr className="bg-ternary-bg">
                      <th
                        className="border border-primary-border"
                        style={{ width: "40px" }}
                      >
                        <Checkbox
                          sx={{ color: "white", padding: "0px", width: "20px" }}
                          onChange={() => {
                            if (selectedRows?.length === sortedData?.length) {
                              dispatch(setSelectedRows([]));
                            } else {
                              dispatch(
                                setSelectedRows(
                                  sortedData?.map(
                                    (_: DataRow, i: number) => i
                                  ) || []
                                )
                              );
                            }
                          }}
                          checked={selectedRows?.length === sortedData?.length}
                        />
                      </th>
                      <th className="px-4 py-4 border border-primary-border">
                        CLUSTER
                      </th>
                      <th className="px-4 py-4 border border-primary-border">
                        {currentSector?.sector === "PFNA" ? "Each UPC" : "UPC"}
                      </th>
                      {currentSector?.sector !== "PFNA" && (
                        <th className="px-4 py-4 border border-primary-border">
                          Description
                        </th>
                      )}
                      <th className="px-4 py-4 border border-primary-border">
                        KEY ACCOUNT
                      </th>
                      <th className="px-4 py-4 border border-primary-border">
                        KEY ACCOUNT NAME
                      </th>
                      <th className="px-4 py-4 border border-primary-border">
                        SALES REGION CODE
                      </th>
                      <th className="px-4 py-4 border border-primary-border">
                        SALES REGION
                      </th>
                      {currentSector?.sector === "PFNA" && (
                        <th className="px-4 py-4 border border-primary-border">
                          SALES ZONE
                        </th>
                      )}
                      <th className="px-4 py-4 border border-primary-border">
                        LOCATION
                      </th>
                      <th className="px-4 py-4 border border-primary-border">
                        MODEL
                      </th>

                      <th className="px-4 py-4 border border-primary-border">
                        RCA STAT
                      </th>
                      <th className="px-4 py-4 border border-primary-border">
                        RCA STAT OVERRIDE
                      </th>
                      <th className="px-4 py-4 border border-primary-border">
                        RCA STAT FINAL
                      </th>
                      <th className="px-4 py-4 border border-primary-border">
                        RCA TAG
                      </th>
                      <th
                        className="px-4 py-4 border border-primary-border cursor-pointer"
                        onClick={() => handleSort("pfeAccuracy")}
                      >
                        <div className="flex items-center justify-between">
                          (PFE) FORECAST ACCURACY STAT{" "}
                          {renderSortIcon("pfeAccuracy")}
                        </div>
                      </th>
                      <th
                        className="px-4 py-4 border border-primary-border cursor-pointer"
                        onClick={() => handleSort("pfeBias")}
                      >
                        <div className="flex items-center justify-between">
                          (PFE) BIAS {renderSortIcon("pfeBias")}
                        </div>
                      </th>
                      <th
                        className="px-4 py-4 border border-primary-border cursor-pointer"
                        onClick={() => handleSort("forecast")}
                      >
                        <div className="flex items-center justify-between">
                          STATISTICAL FORECAST
                          {renderSortIcon("forecast")}
                        </div>
                      </th>
                      <th
                        className="px-4 py-4 border border-primary-border cursor-pointer"
                        onClick={() => handleSort("absError")}
                      >
                        <div className="flex items-center justify-between">
                          ABS ERROR
                          {renderSortIcon("absError")}
                        </div>
                      </th>
                      <th
                        className="px-4 py-4 border border-primary-border"
                        onClick={() => handleSort("dpForecastAccuracy")}
                      >
                        <div className="flex items-center justify-between">
                          (DP) FORECAST ACCURACY STAT
                          {renderSortIcon("dpForecastAccuracy")}
                        </div>
                      </th>
                      <th
                        className="px-4 py-4 border border-primary-border"
                        onClick={() => handleSort("dpForecastBias")}
                      >
                        <div className="flex items-center justify-between">
                          (DP) BIAS
                          {renderSortIcon("dpForecastBias")}
                        </div>
                      </th>
                      <th
                        className="px-4 py-4 border border-primary-border"
                        onClick={() => handleSort("forecastValueAdd")}
                      >
                        <div className="flex items-center justify-between">
                          (DP) FORECAST VALUE ADD
                          {renderSortIcon("forecastValueAdd")}
                        </div>
                      </th>
                      <th className="px-4 py-4 border border-primary-border">
                        RCA TOTAL
                      </th>
                      <th className="px-4 py-4 border border-primary-border">
                        FEATURE & IMPORTANCE
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {sortedData?.map((row: DataRow, idx: number) => {
                      const rowId = row?.id;
                      return (
                        <tr key={rowId} className="border border-gray-200">
                          <td
                            className="border border-primary-border"
                            style={{ width: "40px" }}
                          >
                            <Checkbox
                              sx={{
                                color: "white",
                                padding: "0px",
                                width: "20px",
                                height: "20px",
                              }}
                              checked={selectedRows?.includes(idx)}
                              onChange={() => handleCheckboxChange(idx)}
                            />
                          </td>
                          <td className="px-4 py-4 border border-primary-border">
                            {row.cluster}
                          </td>

                          <td className="px-4 py-4 border border-primary-border">
                            {row.upc}
                          </td>
                          {currentSector?.sector !== "PFNA" && (
                            <td className="px-4 py-4 border border-primary-border">
                              {row.upcDesc}
                            </td>
                          )}

                          <td className="px-4 py-4 border border-primary-border">
                            {row.account}
                          </td>
                          <td className="px-4 py-4 border border-primary-border">
                            {row.keyAccountName}
                          </td>
                          <td className="px-4 py-4 border border-primary-border">
                            {row.region}
                          </td>
                          <td className="px-4 py-4 border border-primary-border">
                            {row.salesRegionName}
                          </td>
                          {currentSector?.sector === "PFNA" && (
                            <td className="px-4 py-4 border border-primary-border">
                              {row.salesZone}
                            </td>
                          )}
                          <td className="px-4 py-4 border border-primary-border">
                            {row.locationName}-{row.locationId}
                          </td>
                          <td className="px-4 py-4 border border-primary-border">
                            {row.model}
                          </td>
                          <td className="px-4 py-4 border border-primary-border">
                            {row.rca}
                          </td>
                          <td className="border border-primary-border">
                            <ThemeProvider theme={theme}>
                              <FormControl
                                fullWidth
                                sx={{
                                  minWidth: "80px",
                                  backgroundColor: "#313131",
                                  borderRadius: "0",
                                }}
                              >
                                <Select
                                  value={
                                    selectValue[rowId] || "Select Override"
                                  }
                                  onChange={(event) =>
                                    handleSelectChange(rowId, event, "summary")
                                  }
                                  sx={{
                                    height: "38px",
                                    "& .MuiInputBase-root": { color: "white" },
                                    "& .MuiOutlinedInput-notchedOutline": {
                                      borderColor: "#616161",
                                    },
                                    "& .MuiSvgIcon-root": { color: "white" },
                                  }}
                                >
                                  <MenuItem
                                    value="Select Override"
                                    key="default-menu-item"
                                  >
                                    Select Override
                                  </MenuItem>
                                  {listOverride?.map((item: any) => (
                                    <MenuItem
                                      value={item}
                                      id={`${uniqueKey("key_")}-id`}
                                      key={`${uniqueKey("key_")}-menu-item-rca`}
                                    >
                                      {item}
                                    </MenuItem>
                                  ))}
                                </Select>
                              </FormControl>
                            </ThemeProvider>
                          </td>
                          <td className="px-4 py-4 border border-primary-border">
                            {row.finalRca}
                          </td>
                          <td
                            className={`px-2 py-2 border border-primary-border ${
                              selectValueTag[row.id] && !row.tag
                                ? "text-gray-400"
                                : "text-white"
                            }`}
                          >
                            <span
                              className={`${
                                selectValueTag[row.id] || row.tag
                                  ? "bg-white rounded px-1 text-black"
                                  : ""
                              }`}
                            >
                              {selectValueTag[row.id] || row.tag || "—"}
                            </span>
                          </td>

                          <td
                            className={`px-4 py-4 border border-primary-border ${getColor(
                              row.pfeAccuracy
                            )}`}
                          >
                            {row.pfeAccuracy}%
                          </td>
                          <td className="px-4 py-4 border border-primary-border">
                            {row.pfeBias}%
                          </td>
                          <td className="px-4 py-4 border border-primary-border">
                            {row.forecast}
                          </td>
                          <td className="px-4 py-4 border border-primary-border">
                            {row.absError}
                          </td>
                          <td
                            className={`px-4 py-4 border border-primary-border ${getColor(
                              row.dpForecastAccuracy
                            )}`}
                          >
                            {row.dpForecastAccuracy}%
                          </td>
                          <td className="px-4 py-4 border border-primary-border">
                            {row.dpForecastBias}%
                          </td>
                          <td className="px-4 py-4 border border-primary-border">
                            {row.forecastValueAdd}
                          </td>
                          <td className="border border-primary-border">
                            <ThemeProvider theme={theme}>
                              <FormControl
                                fullWidth
                                sx={{
                                  minWidth: "80px",
                                  backgroundColor: "#313131",
                                  borderRadius: "0",
                                }}
                              >
                                <Select
                                  value={
                                    selectValueTotal[rowId] ||
                                    row?.rcaTotal ||
                                    "Select Total"
                                  }
                                  onChange={(event) =>
                                    handleSelectChange(rowId, event, "total")
                                  }
                                  sx={{
                                    height: "38px",
                                    "& .MuiInputBase-root": { color: "white" },
                                    "& .MuiOutlinedInput-notchedOutline": {
                                      borderColor: "#616161",
                                    },
                                    "& .MuiSvgIcon-root": { color: "white" },
                                  }}
                                >
                                  <MenuItem
                                    value="Select Total"
                                    key="default-menu-item"
                                  >
                                    Select Total
                                  </MenuItem>
                                  {listOverride?.map((item: any) => (
                                    <MenuItem
                                      value={item}
                                      id={`${uniqueKey("key_")}-id`}
                                      key={`${uniqueKey("key_")}-menu-item-rca`}
                                    >
                                      {item}
                                    </MenuItem>
                                  ))}
                                </Select>
                              </FormControl>
                            </ThemeProvider>
                          </td>
                          <td className="px-4 py-4 border border-primary-border">
                            {row.featureImportance}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="rounded p-2">
                <div className="flex justify-center">
                  <p className="p-2 m-1">
                    No Data to show or no options selected, please choose from
                    the above filters.
                  </p>
                </div>
              </div>
            )}
            {tableData?.length > 0 ? (
              <div className="mt-8 flex justify-center">
                <div className="pt-2">
                  <Pagination
                    totalPages={rcaExecutionPaginationSlice?.totalPages}
                    activePageNumber={rcaExecutionPaginationSlice?.pageNumber}
                    updateBackTestingPageNumber={(data) =>
                      handlePageNumberUpdateEvent(data)
                    }
                  />
                </div>
              </div>
            ) : null}
          </div>
        )}
        {/* Render Toast Notification */}
        {toast.visible && (
          <Toast
            message={toast.message}
            type={toast.type as "success" | "error"}
            onClose={() => setToast({ visible: false, message: "", type: "" })}
          />
        )}
      </TableContainer>
    </div>
  );
};

export default RCATable;
